import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { cn } from '@optools/components/ui/utils';
import { backOff } from 'exponential-backoff';
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { SubscriberEvent, WHEPClient, WHEPClientConfig } from 'red5pro-webrtc-sdk';

export const ERROR_EVENTS = [
  'Subscribe.Connection.Closed',
  'Connect.Failure',
  'Subscribe.Play.Unpublish',
];

export interface Red5PlayerProps {
  className?: string;
  host: string;
  nodeGroup: string;
  onEventError?: (event: SubscriberEvent) => void;
  onSubscribeError?: (error: string) => void;
  streamName: string;
}

export const Red5Player = memo(
  ({ className, host, nodeGroup, onEventError, onSubscribeError, streamName }: Red5PlayerProps) => {
    const mediaElementId = useId();

    const subscriber = useMemo(() => new WHEPClient(), []);
    const [videoError, setVideoError] = useState('');
    const [resubscribing, setResubscribing] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const subscribedRef = useRef(false);
    const subscribingRef = useRef(false);

    const handleEvent = useCallback(
      (event: SubscriberEvent) => {
        setVideoError(event.type);
        onEventError?.(event);
      },
      [onEventError],
    );

    const subscribe = useCallback(
      async (config: WHEPClientConfig): Promise<void> => {
        await subscriber.init(config);
        await subscriber.subscribe();
        subscribedRef.current = true;
        subscribingRef.current = false;
      },
      [subscriber],
    );

    const initializeSubscriber = useCallback(async () => {
      subscribingRef.current = true;

      const endpoint = `https://${host}/as/v1/proxy/whep/live/${streamName}`;
      const config = {
        connectionParams: { nodeGroup },
        endpoint,
        mediaElementId,
        streamName,
      } satisfies WHEPClientConfig;

      await backOff(() => subscribe(config), {
        delayFirstAttempt: true,
        numOfAttempts: 4,
        retry: (error, attemptNumber) => {
          if (attemptNumber === 4) {
            const message =
              typeof error === 'string'
                ? error
                : error instanceof Error
                  ? error.message
                  : 'Red5 Error';

            setVideoError(message);
            setResubscribing(false);
            setRetryCount(0);
            onSubscribeError?.(message);
            subscribedRef.current = false;
            subscribingRef.current = false;
            return false;
          } else {
            setRetryCount(attemptNumber);
            setVideoError(`Subscribe error. Retrying ${attemptNumber}/3...`);
            return true;
          }
        },
        timeMultiple: 3,
      });
    }, [host, mediaElementId, nodeGroup, onSubscribeError, streamName, subscribe]);

    useEffect(() => {
      ERROR_EVENTS.forEach((event) => subscriber.on(event, handleEvent));

      return () => {
        ERROR_EVENTS.forEach((event) => subscriber.off(event, handleEvent));
      };
    }, [handleEvent, subscriber]);

    useEffect(() => {
      if (!subscribingRef.current) {
        initializeSubscriber();
      }

      return () => {
        if (subscribedRef.current) {
          subscriber.unsubscribe().then(() => {
            subscribedRef.current = false;
            subscribingRef.current = false;
          });
        }
      };
    }, [initializeSubscriber, subscriber, handleEvent]);

    const handleResubscribe = () => {
      initializeSubscriber();
      setResubscribing(true);
    };

    const handleCanPlay = () => {
      if (videoError) setVideoError('');
      if (resubscribing) setResubscribing(false);
      if (retryCount > 0) setRetryCount(0);
    };

    return (
      <Card className={cn('relative aspect-video overflow-hidden', className)}>
        {videoError && (
          <div className="absolute top-0 left-0 z-10 flex h-full w-full flex-col items-center justify-center gap-4 bg-muted/75">
            <p className={cn('text-red-600', { 'opacity-0': resubscribing && retryCount === 0 })}>
              {videoError}
            </p>
            {retryCount === 0 && (
              <Button
                disabled={resubscribing}
                loading={resubscribing}
                size="sm"
                onClick={handleResubscribe}>
                Resubscribe
              </Button>
            )}
          </div>
        )}
        <video
          autoPlay
          className="absolute top-0 left-0 h-full w-full object-cover"
          controls
          data-testid="red5-player"
          id={mediaElementId}
          muted
          onCanPlay={handleCanPlay}
        />
      </Card>
    );
  },
);

Red5Player.displayName = 'Red5Player';
